import { useEffect, useRef } from 'react';

const VDOCIPHER_PLAYER_SCRIPT = 'https://player.vdocipher.com/v2/api.js';
const PLAYER_ATTACH_RETRIES = 8;
const PLAYER_ATTACH_DELAY_MS = 500;

let playerScriptPromise = null;

function loadVdoCipherPlayerScript() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.VdoCipher || window.VdoPlayer) return Promise.resolve(true);

  if (!playerScriptPromise) {
    playerScriptPromise = new Promise((resolve) => {
      const existing = document.querySelector(`script[src="${VDOCIPHER_PLAYER_SCRIPT}"]`);
      if (existing) {
        existing.addEventListener('load', () =>
          resolve(Boolean(window.VdoCipher || window.VdoPlayer))
        );
        existing.addEventListener('error', () => resolve(false));
        return;
      }

      const script = document.createElement('script');
      script.src = VDOCIPHER_PLAYER_SCRIPT;
      script.async = true;
      script.onload = () => resolve(Boolean(window.VdoCipher || window.VdoPlayer));
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  return playerScriptPromise;
}

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function resolveVdoCipherPlayer(iframe) {
  if (!iframe) return null;

  if (window.VdoCipher?.api?.getPlayer) {
    return window.VdoCipher.api.getPlayer(iframe);
  }

  if (window.VdoPlayer?.getInstance) {
    return window.VdoPlayer.getInstance(iframe);
  }

  return null;
}

function readVideoNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

async function seekVideoTo(video, seconds) {
  const resumeAt = Math.max(0, Number(seconds) || 0);
  if (!video || resumeAt < 5) return;

  try {
    video.currentTime = resumeAt;
  } catch {
    // Proxy video element may reject direct assignment in some browsers.
  }
}

export function useVdoCipherPlayerEvents({
  iframeRef,
  enabled,
  resumeAtSeconds = 0,
  onTimeUpdate,
  onPause,
  onEnded,
  onDurationKnown,
}) {
  const handlersRef = useRef({
    onTimeUpdate,
    onPause,
    onEnded,
    onDurationKnown,
  });
  const resumeAppliedRef = useRef(false);

  useEffect(() => {
    handlersRef.current = {
      onTimeUpdate,
      onPause,
      onEnded,
      onDurationKnown,
    };
  }, [onDurationKnown, onEnded, onPause, onTimeUpdate]);

  useEffect(() => {
    resumeAppliedRef.current = false;
  }, [resumeAtSeconds, enabled]);

  useEffect(() => {
    if (!enabled || !iframeRef.current) return undefined;

    let disposed = false;
    const cleanups = [];

    const attachPlayerEvents = async () => {
      const loaded = await loadVdoCipherPlayerScript();
      if (disposed || !loaded || !iframeRef.current) return;

      for (let attempt = 0; attempt < PLAYER_ATTACH_RETRIES; attempt += 1) {
        if (disposed || !iframeRef.current) return;

        try {
          const player = await resolveVdoCipherPlayer(iframeRef.current);
          const video = player?.video;
          if (disposed || !video?.addEventListener) {
            await sleep(PLAYER_ATTACH_DELAY_MS);
            continue;
          }

          const handleLoadedMetadata = () => {
            const duration = readVideoNumber(video.duration);
            if (duration > 0) {
              handlersRef.current.onDurationKnown?.(duration);
            }

            if (!resumeAppliedRef.current && resumeAtSeconds > 0) {
              resumeAppliedRef.current = true;
              void seekVideoTo(video, resumeAtSeconds);
            }
          };

          const handleTimeUpdate = () => {
            const currentTime = readVideoNumber(video.currentTime);
            const duration = readVideoNumber(video.duration);
            handlersRef.current.onTimeUpdate?.(currentTime, duration);
          };

          const handlePause = () => {
            handlersRef.current.onPause?.(readVideoNumber(video.currentTime));
          };

          const handleEnded = () => {
            const currentTime = readVideoNumber(video.currentTime);
            const duration = readVideoNumber(video.duration);
            handlersRef.current.onEnded?.(currentTime, duration);
          };

          const handleDurationChange = () => {
            const duration = readVideoNumber(video.duration);
            if (duration > 0) {
              handlersRef.current.onDurationKnown?.(duration);
            }
          };

          video.addEventListener('loadedmetadata', handleLoadedMetadata);
          video.addEventListener('durationchange', handleDurationChange);
          video.addEventListener('timeupdate', handleTimeUpdate);
          video.addEventListener('pause', handlePause);
          video.addEventListener('ended', handleEnded);

          cleanups.push(() => video.removeEventListener('loadedmetadata', handleLoadedMetadata));
          cleanups.push(() => video.removeEventListener('durationchange', handleDurationChange));
          cleanups.push(() => video.removeEventListener('timeupdate', handleTimeUpdate));
          cleanups.push(() => video.removeEventListener('pause', handlePause));
          cleanups.push(() => video.removeEventListener('ended', handleEnded));

          return;
        } catch {
          await sleep(PLAYER_ATTACH_DELAY_MS);
        }
      }
    };

    void attachPlayerEvents().catch(() => {});

    return () => {
      disposed = true;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [enabled, iframeRef, resumeAtSeconds]);
}
